import PropTypes from 'prop-types';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/vi';
import 'dayjs/locale/en';

// Extend dayjs with customParseFormat plugin for parsing time strings
dayjs.extend(customParseFormat);

/**
 * Reusable Time Picker Component
 * Uses Material UI X TimePicker with custom styling
 * Displays formatted time based on culture with clock icon
 *
 * @param {String} label - Time picker label
 * @param {String} value - Selected time value (HH:mm format)
 * @param {Function} onChange - Change handler
 * @param {Boolean} required - Whether field is required
 * @param {Boolean} disabled - Whether time picker is disabled
 * @param {Boolean} fullWidth - Whether to take full width
 * @param {Object} sx - Additional Material UI sx styles
 * @param {Boolean} error - Whether field has an error
 * @param {String} helperText - Helper text below the input
 * @param {String} id - Element id
 * @param {String} name - Element name
 * @param {String} data-testid - Test attribute
 * @param {Boolean} ampm - Whether to use 12-hour format (default: false for 24-hour)
 */
function TimePicker({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  fullWidth = true,
  sx = {},
  error = false,
  helperText = '',
  id,
  name,
  'data-testid': dataTestId,
  ampm = false,
  ...otherProps
}) {
  const { i18n } = useTranslation();

  // Set dayjs locale based on i18n language
  const locale = i18n.language === 'vi' ? 'vi' : 'en';

  // Convert value (HH:mm string) to dayjs object
  const dayjsValue = value ? dayjs(value, 'HH:mm') : null;

  // Handle time change - convert dayjs object back to HH:mm string
  const handleChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      const formattedTime = newValue.format('HH:mm');
      // Create synthetic event to match existing onChange handlers
      const syntheticEvent = {
        target: {
          name: name,
          value: formattedTime,
        },
      };
      onChange(syntheticEvent);
    } else {
      // Invalid time format - pass empty string
      const syntheticEvent = {
        target: {
          name: name,
          value: '',
        },
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <MuiTimePicker
        label={label}
        value={dayjsValue}
        onChange={handleChange}
        disabled={disabled}
        ampm={ampm}
        slotProps={{
          textField: {
            required: required,
            fullWidth: fullWidth,
            error: error,
            helperText: helperText,
            variant: 'outlined',
            size: 'small',
            InputLabelProps: {
              shrink: true,
            },
            sx: {
              '& .MuiInputBase-root': {
                padding: '1px 2px',
                fontSize: '14px',
                lineHeight: '1.5',
              },
              '& .MuiInputBase-input': {
                padding: '7.5px 8px',
                fontSize: '14px',
                lineHeight: '1.5',
              },
              ...sx,
            },
            inputProps: {
              id: id,
              name: name,
              'data-testid': dataTestId,
            },
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              },
              '& .MuiClock-pin': {
                backgroundColor: 'primary.main',
              },
              '& .MuiClockPointer-root': {
                backgroundColor: 'primary.main',
              },
              '& .MuiClockPointer-thumb': {
                backgroundColor: 'primary.main',
                borderColor: 'primary.main',
              },
              '& .MuiClockNumber-root.Mui-selected': {
                backgroundColor: 'primary.main',
              },
            },
          },
        }}
        {...otherProps}
      />
    </LocalizationProvider>
  );
}

TimePicker.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  'data-testid': PropTypes.string,
  ampm: PropTypes.bool,
};

export default TimePicker;
